import { PathParams, rest } from 'msw'
import {
  CreatePortfolioRequest,
  CustomerEarningsSummaryResponse,
  EarningsDistributionResponse,
  GetMonthlyCustomersEarningsResponse,
  GetPortfoliosResponse,
  GetRewardsProductsResponse,
  Portfolio,
  PortfolioStatus,
  PortfolioTransportRequest,
  PortfolioTransportResponse,
  UpdateEarningsDistributionRequest,
} from 'types/rewards'
import mockCreatePortfolio from '../../../__mocks__/Rewards/createPortfolioResponse.json'
import mockDepositToPortfolio from '../../../__mocks__/Rewards/depositToPortfolioResponse.json'
import mockGetCustomerEarningsSummary from '../../../__mocks__/Rewards/getCustomerEarningsSummaryResponse.json'
import mockGetMonthlyCustomersEarnings from '../../../__mocks__/Rewards/getMonthlyCustomersEarningsResponse.json'
import mockGetPortfolios from '../../../__mocks__/Rewards/getPortfoliosResponse.json'
import mockGetRewardsProducts from '../../../__mocks__/Rewards/getRewardsProductsResponse.json'
import mockUpdateEarningsDistribution from '../../../__mocks__/Rewards/updateEarningsDistributionResponse.json'
import mockWithdrawFromPortfolio from '../../../__mocks__/Rewards/withdrawFromPortfolioResponse.json'

const API_URL = process.env.API_URL ?? ''

// Track portfolio poll counts for simulating async activation
const portfolioPollCounts = new Map<string, number>()

// Helper function to reset poll counts (useful for testing)
export const resetPortfolioPollCounts = () => {
  portfolioPollCounts.clear()
}

export const createPortfolio = rest.post<CreatePortfolioRequest, PathParams, Portfolio>(
  `${API_URL}/earn/v1/portfolios`,
  async (req, res, ctx) => {
    const body = await req.json<CreatePortfolioRequest>()

    const portfolioId = `portfolio-${Date.now()}`

    // Initialize poll count for this new portfolio
    portfolioPollCounts.set(portfolioId, 0)

    const response: Portfolio = {
      ...(mockCreatePortfolio as Portfolio),
      reference: body.reference,
      id: portfolioId,
      status: PortfolioStatus.CREATED,
    }

    return res(ctx.status(201), ctx.json(response))
  }
)

export const getPortfolios = rest.get<GetPortfoliosResponse>(
  `${API_URL}/earn/v1/portfolios`,
  async (req, res, ctx) => {
    return res(ctx.status(200), ctx.json(mockGetPortfolios))
  }
)

export const getPortfolio = rest.get<Portfolio>(
  `${API_URL}/earn/v1/portfolios/:portfolioId`,
  async (req, res, ctx) => {
    const { portfolioId } = req.params
    const id = portfolioId as string

    // Get current poll count for this portfolio
    const currentPollCount = portfolioPollCounts.get(id) ?? 0

    // Increment poll count
    portfolioPollCounts.set(id, currentPollCount + 1)

    // Determine status based on poll count
    // First 3 polls (0, 1, 2) return CREATED, 4th poll (3) onwards returns ACTIVE
    const status = currentPollCount < 3 ? PortfolioStatus.CREATED : PortfolioStatus.ACTIVE

    const response: Portfolio = {
      ...(mockCreatePortfolio as Portfolio),
      id,
      status,
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const depositToPortfolio = rest.post<
  PortfolioTransportRequest,
  PathParams,
  PortfolioTransportResponse
>(`${API_URL}/earn/v1/deposits`, async (req, res, ctx) => {
  const body = await req.json<PortfolioTransportRequest>()

  const response: PortfolioTransportResponse = {
    ...(mockDepositToPortfolio as PortfolioTransportResponse),
    reference: body.reference,
  }

  return res(ctx.status(201), ctx.json(response))
})

export const withdrawFromPortfolio = rest.post<
  PortfolioTransportRequest,
  PathParams,
  PortfolioTransportResponse
>(`${API_URL}/earn/v1/withdrawals`, async (req, res, ctx) => {
  const body = await req.json<PortfolioTransportRequest>()

  const response: PortfolioTransportResponse = {
    ...(mockWithdrawFromPortfolio as PortfolioTransportResponse),
    reference: body.reference,
  }

  return res(ctx.status(201), ctx.json(response))
})

export const getRewardsProducts = rest.get<GetRewardsProductsResponse>(
  `${API_URL}/earn/v1/earning-products`,
  async (req, res, ctx) => {
    const pageNumber = Number(req.url.searchParams.get('pageNumber')) || 0
    const pageSize = Number(req.url.searchParams.get('pageSize')) || 20
    const currency = req.url.searchParams.get('currency')

    let filteredProducts = [...(mockGetRewardsProducts as GetRewardsProductsResponse).content]

    if (currency) {
      filteredProducts = filteredProducts.filter(
        product => product.currency.toUpperCase() === currency.toUpperCase()
      )
    }

    const response: GetRewardsProductsResponse = {
      content: filteredProducts.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize),
      totalElements: filteredProducts.length,
      pageable: {
        pageNumber,
        pageSize,
      },
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const getCustomerEarningsSummary = rest.get<CustomerEarningsSummaryResponse>(
  `${API_URL}/earn/v1/customers-earnings-summary`,
  async (req, res, ctx) => {
    const currency = req.url.searchParams.get('currency')

    if (!currency) {
      return res(
        ctx.status(400),
        ctx.json({
          timestamp: new Date().toISOString(),
          status: 400,
          error: 'Bad Request',
          message: 'Currency parameter is required',
          path: '/earn/v1/customers-earnings-summary',
        })
      )
    }

    const response: CustomerEarningsSummaryResponse = {
      ...(mockGetCustomerEarningsSummary as CustomerEarningsSummaryResponse),
      currency,
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const getMonthlyCustomersEarnings = rest.get<GetMonthlyCustomersEarningsResponse>(
  `${API_URL}/earn/v1/monthly-customers-earnings`,
  async (req, res, ctx) => {
    const currency = req.url.searchParams.get('currency')
    const startDate = req.url.searchParams.get('startDate')
    const endDate = req.url.searchParams.get('endDate')
    const pageNumber = Number(req.url.searchParams.get('pageNumber')) || 0
    const pageSize = Number(req.url.searchParams.get('pageSize')) || 20

    if (!currency) {
      return res(
        ctx.status(400),
        ctx.json({
          timestamp: new Date().toISOString(),
          status: 400,
          error: 'Bad Request',
          message: 'Currency parameter is required',
          path: '/earn/v1/monthly-customers-earnings',
        })
      )
    }

    let filteredEarnings = [
      ...(mockGetMonthlyCustomersEarnings as GetMonthlyCustomersEarningsResponse).content,
    ]

    // Filter by currency (case-insensitive)
    filteredEarnings = filteredEarnings.filter(
      earning => earning.currency.toUpperCase() === currency.toUpperCase()
    )

    // Filter by date range
    if (startDate) {
      filteredEarnings = filteredEarnings.filter(
        earning => new Date(earning.earningsPeriodEnd) >= new Date(startDate)
      )
    }

    if (endDate) {
      filteredEarnings = filteredEarnings.filter(
        earning => new Date(earning.earningsPeriodEnd) <= new Date(endDate)
      )
    }

    // Calculate pagination
    const totalElements = filteredEarnings.length
    const totalPages = Math.ceil(totalElements / pageSize)
    const paginatedContent = filteredEarnings.slice(
      pageNumber * pageSize,
      (pageNumber + 1) * pageSize
    )

    const response: GetMonthlyCustomersEarningsResponse = {
      content: paginatedContent,
      page: {
        number: pageNumber,
        size: pageSize,
        totalElements,
        totalPages,
      },
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const updateEarningsDistribution = rest.post(
  `${API_URL}/earn/v1/earnings-distributions`,
  async (req, res, ctx) => {
    const body = await req.json<UpdateEarningsDistributionRequest>()

    // Simulate wrong code validation
    if (body.code !== '123456') {
      return res(
        ctx.status(400),
        ctx.json({
          error: 'Bad Request',
          message: 'Invalid verification code',
          timestamp: new Date().toISOString(),
        })
      )
    }

    const mockedDestinationWalletId = '550e8400-e29b-41d4-a716-446655440000'
    if (body.destinationWalletId !== mockedDestinationWalletId) {
      return res(
        ctx.status(400),
        ctx.json({
          error: 'Bad Request',
          message: 'Invalid destination wallet ID format. Expected UUID',
          timestamp: new Date().toISOString(),
        })
      )
    }

    // Success response
    const response: EarningsDistributionResponse = {
      ...(mockUpdateEarningsDistribution as EarningsDistributionResponse),
      currency: body.currency,
      automatic: body.automatic,
      updatedAt: new Date().toISOString(),
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

const rewardsHandlers = [
  createPortfolio,
  getPortfolios,
  getPortfolio,
  depositToPortfolio,
  withdrawFromPortfolio,
  getRewardsProducts,
  getCustomerEarningsSummary,
  getMonthlyCustomersEarnings,
  updateEarningsDistribution,
]

export default rewardsHandlers
